<template>
  <operaciones-resumen-tab-panel :model="props.data" :tramite="tramite" :idTramite="idTramite" @documentoSubido="onDocumentoSubido" />
</template>

<script setup lang="ts">
import { messageBoxResult } from '@/.shared/components/dialogs/message-box';
import { useModalBuilder, type ToolbarReference } from '@/.shared/helpers/modal-building-helper';
import { useIntl } from '@/.shared/services/i18n/i18nservice';
import type { DxButton } from 'devextreme-vue';
import { getCurrentInstance, onMounted, reactive, type PropType } from 'vue';
import { useRouter } from '@/.shared/services/routing-services';
import OperacionesResumenTabPanel from '@/components/operaciones-energia/OperacionesEnergiaResumenTabPanel.vue';
import { primaryButtonProps } from '@/.shared/helpers/button-helper';
import { useModalService } from '@/.shared/services/modal-service';
import TicketForm from '@/.shared/components/soporte/TicketForm.vue';
import { CreateTicket } from '@/.shared/components/soporte/models/CreateTicket';
import { EstadoOperacionEnergiaEnum, type DetailOperacionEnergiaModel, type TipoSuministroTramiteEnergiaEnum } from '@/.shared/.api';
import { PermissionsApp, useAuthorization } from '@/.shared/services/auth/authorization/authorization-service-impl';
import { useApi } from '@/.shared/.api/integration';

const props = defineProps({
  data: { type: Object as PropType<DetailOperacionEnergiaModel>, required: true },
  tramite: String as () => TipoSuministroTramiteEnergiaEnum,
  idTramite: String as () => String,
});
const intl = useIntl();
const router = useRouter();
const modal = useModalService();
const autho = useAuthorization();
const api = useApi();
let model = reactive<DetailOperacionEnergiaModel>({} as DetailOperacionEnergiaModel);
  
onMounted(async () => {
  model = props.data;
  const modalBuilder = useModalBuilder();
  modalBuilder.setContext(getCurrentInstance()!)

  configureButtons(modalBuilder);
});

function configureButtons(modalBuilder: ReturnType<typeof useModalBuilder>) {
  let okButton: ToolbarReference<DxButton>;
  let cancelButton: ToolbarReference<DxButton>;
  let chatButton: ToolbarReference<DxButton>;
  let soporteButton: ToolbarReference<DxButton>;
  let editar = `${intl.i18n.t("operaciones.detalle.editar")}`;
  const esPendienteFirma = props.data?.IdEstado === EstadoOperacionEnergiaEnum.PdtFirma;
  const esPendienteRecepcionConsentimiento = props.data?.IdEstado === EstadoOperacionEnergiaEnum.PdtRecepcionConsentimiento;
  const puedeAccederAEdicion = props.data?.EsEditable || esPendienteFirma || esPendienteRecepcionConsentimiento;
  ({ okButton, cancelButton } = modalBuilder.standardButtons.addOkCancel());
  chatButton = modalBuilder.addButton("irAChat", { ...primaryButtonProps(intl.i18n.t("operaciones.grid.ir_a_chat")),
    type: "success", icon: "fa-regular fa-comment-dots" }, { toolbar: "bottom", location: "before" });
  chatButton.update(w => w.onClick = () => irAChat());
  soporteButton = modalBuilder.addButton("soporte", { ...primaryButtonProps(intl.i18n.t('operaciones.detalle.contactar_soporte')), 
      type: 'success', icon: "fa-thin fa-headset"}, { toolbar: "bottom", location: "before" });
  okButton.update(w => w.onClick = async () => {
    if (props.data?.IdEstado === EstadoOperacionEnergiaEnum.PdtFirma) {
      await modal.messageBox(
        intl.i18n.t("operaciones.detalle.editar_pdt_firma_mensaje"),
        intl.i18n.t("operaciones.detalle.editar_pdt_firma_titulo"),
        "ok",
        "info",
      );
      return;
    }
    await router.push(intl.i18Route({ name: "nueva-venta", params: { "id": props.data?.Id } }));
    modalBuilder.close(messageBoxResult.ok);
  }
  );
  okButton.update(w => w.text = editar);
  okButton.update(w => w.disabled = !puedeAccederAEdicion);
  okButton.update(w => w.visible = autho.hasPermission(PermissionsApp.ViewNuevaVenta));
  cancelButton.update(w => w.onClick = () => modalBuilder.close(messageBoxResult.cancel));
  soporteButton.update(w => w.onClick = () => crearTicket())
}

function irAChat(): void {
  const rutaColaboradores = import.meta.env.VITE_BASE_URL;
  const codigo = (props.data.CodigoSecuencial ?? 0).toString().padStart(10, "0");
  const tipoRuta = props.tramite ? props.tramite : "ENERGIA";
  const url = `${rutaColaboradores}/comunicaciones-operacion/${props.data.Id}/${codigo}/${tipoRuta}`;
  window.open(url, "_blank");
}

async function crearTicket() {
  let title = `${intl.i18n.t("ticket.creacion.titulo")}`;
  const ticket = CreateTicket.CreateOperacionTicket(props.data?.Id!, props.data?.CodigoSecuencial?.toString() ?? '', props.tramite ?? '');
  await modal.showModal(
    TicketForm,
    { model: ticket },
    { title: title, width: 800, height: 700 }
  );
}

const onDocumentoSubido = async() => {
  var response = await api.callStandardApi("getOperacionEnergiaDetail", { showLoading: true }, props.data!.Id);
  Object.assign(model, response); 
}

</script>

<style lang="scss" scoped></style>